import React, { useState } from 'react';
import { motion } from 'framer-motion';

const PulsingText = ({ children, delay = 0, isRed = false, isBold = false }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <motion.span
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`cursor-default transition-all duration-700 ${isBold ? 'font-serif italic' : ''}`}
      style={{
        color: isRed
          ? hovered ? 'rgba(155,66,78,1)' : 'rgba(155,66,78,0.85)'
          : hovered ? 'rgba(212,175,55,0.95)' : 'inherit',
        textShadow: hovered
          ? isRed
            ? '0 0 15px rgba(155,66,78,0.8)'
            : '0 0 15px rgba(212,175,55,0.8)'
          : 'none',
        transition: 'all 0.7s ease',
      }}
    >
      {children}
    </motion.span>
  );
};

const Paragraph = ({ children, delay }) => (
  <motion.p
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-60px' }}
    transition={{ duration: 0.9, delay }}
    className="text-lg md:text-xl font-light leading-relaxed text-cream/65"
  >
    {children}
  </motion.p>
);

const DeepMirror = () => (
  <section
    id="spiegel"
    className="relative py-36 bg-darker overflow-hidden border-t border-white/5"
  >
    {/* Pulsing red ambient */}
    <motion.div
      animate={{ opacity: [0, 0.12, 0] }}
      transition={{ duration: 6, repeat: Infinity }}
      className="absolute inset-0 pointer-events-none"
      style={{
        background:
          'radial-gradient(ellipse at 0% 50%, rgba(114,47,55,0.35) 0%, transparent 45%), radial-gradient(ellipse at 100% 50%, rgba(114,47,55,0.35) 0%, transparent 45%)',
      }}
    />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_50%,rgba(212,175,55,0.03)_0%,transparent_70%)]" />

    <div className="relative z-10 max-w-3xl mx-auto px-6">
      <motion.p
        initial={{ opacity: 0, letterSpacing: '0.1em' }}
        whileInView={{ opacity: 1, letterSpacing: '0.4em' }}
        viewport={{ once: true }}
        transition={{ duration: 1.5 }}
        className="text-wine-light uppercase text-xs font-semibold mb-12 text-center tracking-[0.35em]"
      >
        Der Spiegel der Wahrheit
      </motion.p>

      <motion.h2
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.9 }}
        className="text-4xl md:text-6xl font-serif text-cream italic leading-tight mb-16 text-center"
      >
        Ich sehe dich.{' '}
        <span className="text-wine-light block mt-2" style={{ textShadow: '0 0 20px rgba(155,66,78,0.35)' }}>
          Vollständig.
        </span>
      </motion.h2>

      <div className="space-y-10">
        <Paragraph delay={0.1}>
          Es ist{' '}
          <PulsingText isRed>23:47 Uhr</PulsingText>. Du liegst im Dunkeln, das Telefon auf
          der Brust, den Bildschirm entsperrt. Du wartest auf eine Nachricht, die vielleicht
          kommt. Vielleicht. Dieses Wort hat sich wie ein{' '}
          <PulsingText isRed isBold>Stachel</PulsingText> tief in dein Nervensystem gebrannt.
          Nicht Ja. Nicht Nein. Immer nur{' '}
          <PulsingText isRed>Vielleicht</PulsingText>. Und in diesem Vielleicht lebst du.
          Zwischen den Brotkrumen seiner Aufmerksamkeit, die er großzügig streut – genug, um
          dich zu halten, aber niemals genug, um dich wirklich zu sättigen.
        </Paragraph>

        <Paragraph delay={0.2}>
          Du kennst diesen Moment, wenn er sich endlich meldet. Wie sich{' '}
          <PulsingText>deine Brust öffnet</PulsingText>, wie der{' '}
          <PulsingText isRed>Kloß im Hals</PulsingText> sich auflöst, wie deine Schultern
          für einen einzigen Atemzug von dem Gewicht befreit werden, das du ständig trägst.
          Dopamin. Oxytocin. Dein Gehirn behandelt seine Nachricht wie eine Droge – und du
          bist seit Monaten, vielleicht Jahren, abhängig. Du hasst dich dafür. Du liebst ihn
          dafür. Beides gleichzeitig, in einem schmerzhaften Wirbel, der dich nicht schlafen
          lässt und tagsüber nicht wirklich wach sein lässt.
        </Paragraph>

        <Paragraph delay={0.3}>
          Du analysierst. Gott, wie du analysierst. Jede seiner Nachrichten wird wie ein
          heiliger Text gelesen, nach versteckten Bedeutungen durchsucht. War das Emoji
          wärmer als gestern? Hat er die Antwort absichtlich verzögert? Was meinte er mit
          "Ich muss nachdenken"? Du bist brillant. Du bist intuitiv. Du bist eine Frau, die
          in ihrem Beruf Entscheidungen trifft, die andere nicht treffen können. Und trotzdem
          sitzt du hier, um{' '}
          <PulsingText isRed isBold>3 Uhr morgens</PulsingText>, und versuchst, das
          Verhalten eines Mannes zu entschlüsseln, der dir nicht die Wahrheit sagen kann –
          weil er sie selbst nicht kennt.
        </Paragraph>

        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.35 }}
          className="relative p-8 md:p-12"
          style={{
            background: 'rgba(114,47,55,0.06)',
            border: '1px solid rgba(114,47,55,0.2)',
            borderLeft: '3px solid rgba(155,66,78,0.6)',
          }}
        >
          <p className="text-xl md:text-2xl font-serif text-cream italic leading-relaxed">
            "Du gibst. Du biegst dich. Du machst dich kleiner. Du streichst deine eigenen
            Bedürfnisse durch wie Bleistiftnotizen, die ohnehin niemand liest. Und tief in
            dir, unter all dem Verständnis und der Geduld, brennt eine{' '}
            <span className="text-wine-light">stille, verzweifelte Wut</span> – auf ihn,
            auf dich, auf die Situation, die sich anfühlt wie ein goldener Käfig ohne
            Schlüssel."
          </p>
        </motion.div>

        <Paragraph delay={0.4}>
          Dein Körper weiß es bereits. Die{' '}
          <PulsingText isRed>Enge in deiner Brust</PulsingText>, wenn sein Name im Display
          auftaucht – dieser Cocktail aus Freude und Angst, der sich nicht mehr unterscheiden
          lässt. Das{' '}
          <PulsingText isRed>ständige Auf-der-Hut-Sein</PulsingText>, das Scannen seiner
          Energie, seiner Laune, seiner Verfügbarkeit. Du hast gelernt, dich auf ihn zu
          kalibrieren wie ein Seismograph auf Erdbeben. Du spürst seine Stimmung, bevor er
          ein Wort gesagt hat. Du passt dich an. Du regulierst. Du trägst nicht nur deine
          eigenen Gefühle – du trägst auch seine. Das erschöpft nicht nur deine Seele.{' '}
          <PulsingText isBold>Das erschöpft dein Nervensystem auf zellulärer Ebene.</PulsingText>
        </Paragraph>

        <Paragraph delay={0.5}>
          Und das Schmerzhafteste? Du weißt es. Tief unter den Erklärungen, den
          Rechtfertigungen, den "Aber er hat auch gute Seiten" und den "Ich verstehe, er hat
          eine schwierige Vergangenheit" – weißt du, dass sich{' '}
          <PulsingText isRed isBold>Liebe nicht so anfühlen soll</PulsingText>. Echte Liebe
          soll sich nicht wie ein Überlebensprojekt anfühlen. Sie soll sich wie{' '}
          <PulsingText>Heimkommen</PulsingText> anfühlen. Wie Atem. Wie Stille.
          Dieses Wissen hat dich hierher geführt.
        </Paragraph>
      </div>

      <motion.div
        initial={{ opacity: 0, scaleX: 0 }}
        whileInView={{ opacity: 1, scaleX: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.5, delay: 0.5 }}
        className="w-full h-[1px] mt-20 origin-center"
        style={{ background: 'linear-gradient(to right, transparent, rgba(212,175,55,0.4), transparent)' }}
      />
    </div>
  </section>
);

export default DeepMirror;